import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [licenseExpired, setLicenseExpired] = useState(false);
  const idleTimer = useRef(null);

  const logout = useCallback(async () => {
    try { await api.post('/api/auth/logout'); } catch (_) {}
    setUser(null);
    setLicenseExpired(false);
    window.location.href = '/login';
  }, []);

  // Reset idle timer on any user activity
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(logout, IDLE_TIMEOUT_MS);
  }, [logout]);

  // Attach / detach idle listeners whenever a user is logged in
  useEffect(() => {
    if (!user) {
      clearTimeout(idleTimer.current);
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, resetIdleTimer));
      return;
    }
    IDLE_EVENTS.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer(); // start the first timer
    return () => {
      clearTimeout(idleTimer.current);
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, resetIdleTimer));
    };
  }, [user, resetIdleTimer]);

  // On app load, validate session and check license status (desktop).
  useEffect(() => {
    api.get('/api/auth/me', { validateStatus: s => s < 500 })
      .then(async res => {
        if (res.status === 200) {
          setUser(res.data);
          // Restore license-expired state for existing sessions
          if (isElectron) {
            try {
              const status = await window.electronAPI.getLicenseStatus();
              if (!status.valid) setLicenseExpired(true);
            } catch (_) {}
          }
        } else {
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen for mid-session license status changes from main process
  useEffect(() => {
    if (!isElectron || !user) return;
    const unlisten = window.electronAPI.onLicenseStatusChanged?.((newStatus) => {
      setLicenseExpired(!newStatus.valid);
    });
    return () => unlisten?.();
  }, [user]);

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    setUser(response.data.user);
    return response.data.user;
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, isAuthenticated,
      licenseExpired, setLicenseExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
